import { hasValue } from 'Lib/Any';
import { format as d3format, formatSpecifier } from 'd3-format';
import Decimal from 'decimal.js-light';
import moment from 'moment';
import { FormatSpecifier } from '.';
import { MidpointRoundingType, roundFixedPoint, roundSf } from '../Round';
import { FormatTypes } from './formatType';

/**
 * Returns a formatting function for the given format specifier.
 * @param {FormatSpecifier | ((value: any) => FormatSpecifier)} specifier - A format specifier
 * or a function returning a format specifier for a given value.
 *
 * @example <caption>Fixed point formatting</caption>
 * // returns '1,234'
 * format({ type: FormatTypes.FixedPoint, nearest: 1 })(1234.5)
 * @example <caption>Significant figures formatting</caption>
 * // returns '0.11'
 * format({ type: FormatTypes.SignificantFigures, precision: 2 })(0.11000011)
 * @example <caption>Date/time formatting</caption>
 * // returns '2019-03-25'
 * format({ type: FormatTypes.DateTime, pattern: 'YYYY-MM-DD' })(new Date('2019-03-25T00:18:13.457Z'))
 */
const format =
  (specifier: FormatSpecifier | ((value: any) => FormatSpecifier) = {}) =>
  (value: any) => {
    // Allows us to coalesce on the result if we need to, e.g. let value = format({...})(null) ?? "No value"
    if (value == null) return null;

    if (typeof specifier === 'function') {
      specifier = specifier(value);
    }

    let {
      midpoint = MidpointRoundingType.ToEven,
      nearest,
      precision,
      pattern,
      type = FormatTypes.FixedPoint,
    } = specifier;

    // Do not show thousands comma for UK language (en-GB)
    let thousandsComma = navigator.language !== 'en-GB';
    if (type === FormatTypes.Time) {
      // We need to handle ISO 8601 date + time values that come from API models serialized using
      // `Contracts.UnspecifiedLocalDateTimeConverter` (or from time inputs that use
      // `dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}`). We also need to handle
      // "plain" time values that come from API models serialized using `Contracts.TimeConverter`,
      // (or from time inputs that use `dataFormat={DateTimeDataFormat.Time}`).
      //
      // Values that are "time only" are not parsed by moment.js without specifying a format,
      // so we also need to parse the value using the specific time formats that we support.
      const time = moment(value || null, ['HH:mm:ss.SSS', 'HH:mm:ss', 'HH:mm'], true);
      if (time.isValid()) return time.format(pattern ?? 'HH:mm');
      const date = moment(value || null); // undefined goes to "today" if you don't replace it
      return date.isValid() ? date.format(pattern ?? 'HH:mm') : '';
    }
    if (type === FormatTypes.Date) {
      const date = moment(value || null); // undefined goes to "today" if you don't replace it
      return date.isValid() ? date.format(pattern ?? getDefaultDateFormatString()) : '';
    }
    if (type === FormatTypes.DateTime) {
      const date = moment(value || null); // undefined goes to "today" if you don't replace it
      return date.isValid() ? date.format(pattern ?? getDefaultDateFormatString() + ' HH:mm') : '';
    }

    if (type === FormatTypes.YesNo) {
      if (typeof value !== 'boolean') throw new Error('Cannot format value, value is not a boolean.');

      return !!value ? 'Yes' : 'No';
    }

    if (typeof value !== 'number') throw new Error('Cannot format value, value is not a number.');

    if (type === FormatTypes.SignificantFigures) {
      return formatSf(value, precision, midpoint, thousandsComma);
    }

    if (type === FormatTypes.FixedPoint) {
      if (!nearest) {
        if (hasValue(precision) && precision! >= 0) nearest = 1 / Math.pow(10, precision!);
        else nearest = 1;
      }
      return formatFixedPoint(value, nearest, midpoint, thousandsComma);
    }

    if (type === FormatTypes.AsEntered) {
      return value;
    }

    throw new Error('Formatting type not supported.');
  };

const formatFixedPoint = (value: any, nearest: number, midpoint: MidpointRoundingType, thousandsComma: boolean) => {
  const precision = new Decimal(nearest).decimalPlaces();

  let d3specifier = formatSpecifier('f');
  d3specifier.precision = precision;
  d3specifier.comma = thousandsComma;
  const formatter = d3format(d3specifier as any);

  return formatter(roundFixedPoint(value, nearest, midpoint));
};
//helpers
//To get default date format string based on browser's locale
//E.g 'en-US' will return MM/DD/YYYY, 'en-AU' will return 'DD/MM/YYYY'
const getDefaultDateFormatString = () => {
  //window.navigator.langauge will return the preferred language of the browser
  const formatObj = new Intl.DateTimeFormat(window.navigator.language).formatToParts(new Date());
  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return 'DD';
        case 'month':
          return 'MM';
        case 'year':
          return 'YYYY';
        default:
          return obj.value;
      }
    })
    .join('');
};

const formatSf = (
  value: any,
  precision: number | undefined,
  midpoint: MidpointRoundingType,
  thousandsComma: boolean
) => {
  if (!precision || precision <= 0) precision = 6;

  let d3specifier = formatSpecifier('r');
  d3specifier.precision = precision;
  d3specifier.comma = thousandsComma;
  const formatter = d3format(d3specifier as any);

  return formatter(roundSf(value, precision, midpoint));
};

export default format;
